import { useMemo } from 'react';

import { WizardProgress, WizardScreen } from '@blocks';
import { useTranslate } from '@i18n';
import { ElvaLogoName, Stack, Text, u } from '@ui';

import {
    AgeStep,
    CoachStep,
    GolfBagStep,
    HandednessStep,
    HandicapStep,
    LanguageStep,
    SexStep,
    UnitStep,
} from './components';
import { useSetLocaleAtom } from './useSetLocaleAtom';
import { useUserWelcomeWizard } from './useUserWelcomeWizard';

type WelcomeStep = {
    id: (typeof WIZARD_STEPS)[keyof typeof WIZARD_STEPS];
    label: string;
    title: string;
    description?: string;
    subHeader: string;
    isComplete: boolean;
};

const WIZARD_STEPS = {
    LANGUAGE: 'language',
    AGE: 'age',
    SEX: 'sex',
    HANDICAP: 'handicap',
    UNIT: 'unit',
    BAG: 'bag',
    HANDEDNESS: 'handedness',
    COACH: 'coach',
} as const;

const TOTAL_STEPS = Object.keys(WIZARD_STEPS).length;

type UserWelcomeWizardProps = {
    onDismiss: () => void;
};

export const UserWelcomeWizard = ({ onDismiss }: UserWelcomeWizardProps) => {
    const t = useTranslate();
    const {
        form,
        activeStepIndex,
        clubs,
        coachOptions,
        isLoadingClubs,
        isLoadingUserClubs,
        isLoadingCoaches,
        isLoadingUserCoaches,
        isSaving,
        persistAndDismiss,
        handleNext,
        handleBack,
    } = useUserWelcomeWizard({ totalSteps: TOTAL_STEPS, onDismiss });
    const selectedLocale = form.watch('locale');
    const selectedDob = form.watch('dateOfBirth');
    const selectedHandicap = form.watch('handicap');
    const selectedUnit = form.watch('userPreferredUnit');
    const selectedHandedness = form.watch('handedness');

    useSetLocaleAtom(form);

    const steps: WelcomeStep[] = useMemo(
        () => [
            {
                id: WIZARD_STEPS.LANGUAGE,
                label: t('user_welcome_wizard.language_step_label'),
                title: t('user_welcome_wizard.language_title'),
                subHeader: t('user_welcome_wizard.language_description'),
                isComplete: !!selectedLocale,
            },
            {
                id: WIZARD_STEPS.AGE,
                label: t('user_welcome_wizard.age_step_label'),
                title: t('user_welcome_wizard.age_title'),
                description: t('user_welcome_wizard.age_description'),
                subHeader: t('user_welcome_wizard.age_description'),
                isComplete: selectedDob instanceof Date && !Number.isNaN(selectedDob.getTime()),
            },
            {
                id: WIZARD_STEPS.SEX,
                label: t('user_welcome_wizard.sex_step_label'),
                title: t('user_welcome_wizard.sex_title'),
                description: t('user_welcome_wizard.sex_optional'),
                subHeader: t('user_welcome_wizard.sex_description'),
                isComplete: true,
            },
            {
                id: WIZARD_STEPS.HANDICAP,
                label: t('user_welcome_wizard.handicap_step_label'),
                title: t('user_welcome_wizard.handicap_title'),
                subHeader: t('user_welcome_wizard.handicap_description'),
                isComplete: typeof selectedHandicap === 'number' && Number.isFinite(selectedHandicap),
            },
            {
                id: WIZARD_STEPS.HANDEDNESS,
                label: t('user_welcome_wizard.handedness_step_label'),
                title: t('user_welcome_wizard.handedness_title'),
                subHeader: t('user_welcome_wizard.handedness_description'),
                isComplete: !!selectedHandedness,
            },
            {
                id: WIZARD_STEPS.UNIT,
                label: t('user_welcome_wizard.unit_step_label'),
                title: t('user_welcome_wizard.unit_title'),
                subHeader: t('user_welcome_wizard.unit_description'),
                isComplete: !!selectedUnit,
            },
            {
                id: WIZARD_STEPS.BAG,
                label: t('user_welcome_wizard.bag_step_label'),
                title: t('user_welcome_wizard.bag_title'),
                subHeader: t('user_welcome_wizard.bag_description'),
                isComplete: true,
            },
            {
                id: WIZARD_STEPS.COACH,
                label: t('user_welcome_wizard.coach_step_label'),
                title: t('user_welcome_wizard.coach_title'),
                description: t('user_welcome_wizard.coach_optional_disclaimer'),
                subHeader: t('user_welcome_wizard.coach_description'),
                isComplete: true,
            },
        ],
        [selectedDob, selectedHandedness, selectedHandicap, selectedLocale, selectedUnit, t],
    );

    const currentStep = steps[activeStepIndex];
    const currentStepId = currentStep.id;
    const currentStepNumber = activeStepIndex + 1;
    const totalSteps = steps.length;
    const isLastStep = currentStepNumber === totalSteps;
    const canGoBack = currentStepNumber > 1;
    const canGoNext = currentStep.isComplete;
    const isFinishDisabled = !currentStep.isComplete;
    const canFinish = steps.every((d) => d.isComplete);
    return (
        <WizardScreen.Layout>
            <WizardScreen.Header start={<ElvaLogoName width={116} />}>
                {t('user_welcome_wizard.welcome_title')}
            </WizardScreen.Header>
            <WizardProgress
                title={currentStep.label}
                subHeader={currentStep.subHeader}
                currentStep={currentStepNumber}
                totalSteps={totalSteps}
            />
            <WizardScreen.Content>
                <Stack gap={u(3)}>
                    <Stack gap={u(1.5)} alignItems="center" pb={u(2)}>
                        <Text variant="h2">{currentStep.title}</Text>
                        {currentStep.description ? (
                            <Text variant="h3" color="muted">
                                {currentStep.description}
                            </Text>
                        ) : null}
                    </Stack>

                    {currentStepId === WIZARD_STEPS.LANGUAGE && <LanguageStep control={form.control} />}
                    {currentStepId === WIZARD_STEPS.AGE && <AgeStep control={form.control} />}
                    {currentStepId === WIZARD_STEPS.SEX && <SexStep control={form.control} />}
                    {currentStepId === WIZARD_STEPS.HANDICAP && <HandicapStep control={form.control} />}
                    {currentStepId === WIZARD_STEPS.HANDEDNESS && <HandednessStep control={form.control} />}
                    {currentStepId === WIZARD_STEPS.UNIT && <UnitStep control={form.control} />}
                    {currentStepId === WIZARD_STEPS.BAG && (
                        <GolfBagStep
                            control={form.control}
                            clubs={clubs}
                            isLoading={isLoadingClubs || isLoadingUserClubs}
                        />
                    )}

                    {currentStepId === WIZARD_STEPS.COACH && (
                        <CoachStep
                            control={form.control}
                            coachOptions={coachOptions}
                            isLoading={isLoadingCoaches || isLoadingUserCoaches}
                        />
                    )}
                </Stack>
            </WizardScreen.Content>
            <WizardScreen.Footer
                isLastStep={isLastStep}
                canGoNext={canGoNext}
                canGoBack={canGoBack}
                isFinishDisabled={isFinishDisabled}
                isFinishLoading={isSaving}
                canFinish={canFinish}
                finishLabel={t('generic_ui.finish')}
                onNext={handleNext}
                onBack={handleBack}
                onFinish={persistAndDismiss}
            />
        </WizardScreen.Layout>
    );
};
